import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Popover,
  Radio,
  ScrollArea,
  Stack,
  Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import Select, { StylesConfig } from 'react-select';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGetGlobalFilterQuery,
  useGetPractitionersByClinicIdQuery,
  useUpdateGlobalFilterMutation
} from '@/store/slices/api/userApiSlice';
import { setFilters as setGlobalFilters } from '@/store/slices/preferencesSlice';
import { delay } from '@/utils';
import { NULL_OPTION } from '@/utils/constants';
import useSaveUpdatedReportFields from '@/hooks/useSaveUpdatedReportFields';
import InputLabel from '@/components/InputLabel';
import MultiSelectDropdownWithSearch from '@/components/MultiSelectDropdownWithSearch';
import FilterMenuIcon from '@/assets/icons/FilterMenuIcon';
import { plum } from '@/styles/colors';

import { LabelAndValue } from '@/types/custom';
import { DeviceType, DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { UserRole } from '@/types/user';
import LabelText from '@/ui/LabelText';

interface CombinedFiltersDropdownProps {
  iconStyle?: React.CSSProperties;
  customFilterIcon?: React.ReactNode;
  selectedCurrentReportFilters?: string[];
  selectedPreviousReportFilters?: string[];
  isNonPractitionerSignedCRSelected?: boolean;
  isPendingReviewSelectedCRSelected?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
  currentReportType?: 'current' | 'previous';
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
  onApplyReportFilters?: (
    filters: string[],
    isNonPractitioner: boolean,
    isPendingReview: boolean,
    isRecentSigned: boolean,
    isNotDownloaded: boolean,
    reportType: 'current' | 'previous',
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => void;
  showNoReportOption?: boolean;
  showPreviousReportOptions?: boolean;
}

interface FilterState {
  clinics: LabelAndValue[];
  practitioners: LabelAndValue[];
  signingPractitioners: LabelAndValue[];
  deviceTypes: LabelAndValue[];
  vendors: LabelAndValue[];
  patientStatuses: LabelAndValue[];
}

const FilterOption = memo(
  ({
    options,
    defaultValues,
    onChange,
    label,
    isLoading
  }: {
    options: LabelAndValue[];
    defaultValues: LabelAndValue[];
    onChange: (values: LabelAndValue[]) => void;
    label: string;
    isLoading?: boolean;
  }) => (
    <MultiSelectDropdownWithSearch
      options={options}
      defaultValues={defaultValues}
      onChange={onChange}
      label={label}
      maxItemsToDisplay={1}
      isLoading={isLoading}
    />
  )
);

FilterOption.displayName = 'FilterOption';

const DEVICE_TYPE_OPTIONS: LabelAndValue[] = [
  NULL_OPTION,
  ...Object.values(DeviceType).map((deviceType) => ({
    label: deviceType,
    value: deviceType
  }))
];

const VENDOR_OPTIONS: LabelAndValue[] = Object.values(DeviceVendor).map((vendor) => ({
  label: getVendorDisplayName(vendor),
  value: vendor
}));

const PATIENT_STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Paused', value: 'Paused' }
];

const FILTER_OPTIONS = [
  { value: 'GREEN', label: 'Routine' },
  { value: 'YELLOW', label: 'Yellow Alert' },
  { value: 'RED', label: 'Red Alert' }
];

const REPORT_TYPE_OPTIONS: LabelAndValue[] = [
  { value: 'none', label: 'No selection' },
  { value: 'current', label: 'Current Reports' },
  { value: 'previous', label: 'Previous Reports' }
];

const reportTypeSelectStyles: StylesConfig<LabelAndValue, false> = {
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: 'white',
    borderColor: isFocused ? plum[6] : plum[3],
    '&:hover': {
      borderColor: plum[5]
    },
    cursor: 'pointer',
    minHeight: '35px'
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    backgroundColor: isSelected ? plum[5] : isFocused ? plum[2] : 'transparent',
    color: isSelected ? 'white' : plum[8],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: isSelected ? plum[6] : plum[2]
    }
  }),
  singleValue: (styles) => ({
    ...styles,
    color: plum[9]
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: plum[6],
    ':hover': {
      color: plum[7]
    }
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    backgroundColor: plum[4]
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};

const CombinedFiltersDropdown = ({
  selectedCurrentReportFilters = [],
  selectedPreviousReportFilters = [],
  isNonPractitionerSignedCRSelected = false,
  isPendingReviewSelectedCRSelected = false,
  isRecentSignedSelected = false,
  isDownloadedSelected = false,
  currentReportType = 'current',
  recentSignedSortBy: initialRecentSignedSortBy = 'SIGNED_DATE',
  onApplyReportFilters,
  showNoReportOption = true,
  showPreviousReportOptions = true
}: CombinedFiltersDropdownProps) => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure();
  const { filters: globalFilters } = useAppSelector((state) => state.preferences);
  const { reportUpdateFields } = useAppSelector((state) => state.transmissionReports);
  const { user } = useAppSelector((state) => state.auth);
  const saveUpdatedReportFields = useSaveUpdatedReportFields();

  const userRole = user?.role;
  const isUserAdmin =
    userRole === UserRole.InternalSuperAdmin ||
    userRole === UserRole.InternalAdmin ||
    userRole === UserRole.InternalNonClinical ||
    userRole === UserRole.InternalClinical;

  const [updateGlobalFilter, { isLoading: isUpdatingFilter }] = useUpdateGlobalFilterMutation();
  const { data: globalFilterData } = useGetGlobalFilterQuery();

  const isDropdownOpenRef = useRef(false);

  const {
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses
  } = useMemo(
    () => ({
      defaultClinics: globalFilters?.clinics ?? [],
      defaultPractitioners: globalFilters?.practitioners ?? [],
      defaultSigningPractitioners: globalFilters?.signingPractitioners ?? [],
      defaultDeviceTypes: globalFilters?.deviceTypes ?? [],
      defaultVendors: globalFilters?.vendors ?? [],
      defaultPatientStatuses: globalFilters?.patientStatuses ?? []
    }),
    [globalFilters]
  );

  const [filters, setFilters] = useState<FilterState>({
    clinics: defaultClinics,
    practitioners: defaultPractitioners,
    signingPractitioners: defaultSigningPractitioners,
    deviceTypes: defaultDeviceTypes,
    vendors: defaultVendors,
    patientStatuses: defaultPatientStatuses
  });

  const [reportType, setReportType] = useState<'current' | 'previous' | null>(currentReportType);
  const [tempCurrentReportFilters, setTempCurrentReportFilters] = useState<string[]>(
    selectedCurrentReportFilters
  );
  const [tempPreviousReportFilters, setTempPreviousReportFilters] = useState<string[]>(
    selectedPreviousReportFilters
  );
  const [tempNonPractitionerSelected, setTempNonPractitionerSelected] = useState(
    isNonPractitionerSignedCRSelected
  );
  const [tempPendingReviewSelected, setTempPendingReviewSelected] = useState(
    isPendingReviewSelectedCRSelected
  );
  const [tempRecentSignedSelected, setTempRecentSignedSelected] = useState(isRecentSignedSelected);
  const [tempDownloadedSelected, setTempDownloadedSelected] = useState(isDownloadedSelected);
  const [recentSignedSortBy, setRecentSignedSortBy] = useState<'SIGNED_DATE' | 'DATE_OF_SERVICE'>(
    initialRecentSignedSortBy
  );
  const [showRecentSignedSubmenu, setShowRecentSignedSubmenu] = useState(false);

  const {
    data: practitionerData,
    isLoading: practitionerOptionsLoading,
    isFetching: practitionerOptionsFetching
  } = useGetPractitionersByClinicIdQuery(
    {
      clinicIds: filters.clinics.map((clinic) => clinic.value)
    },
    {
      skip: filters.clinics.length === 0
    }
  );

  useEffect(() => {
    if (!isDropdownOpenRef.current) {
      setFilters({
        clinics: defaultClinics,
        practitioners: defaultPractitioners,
        signingPractitioners: defaultSigningPractitioners,
        deviceTypes: defaultDeviceTypes,
        vendors: defaultVendors,
        patientStatuses: defaultPatientStatuses
      });
    }
  }, [
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses
  ]);

  useEffect(() => {
    if (!isDropdownOpenRef.current) {
      let actualReportType: 'current' | 'previous' | null = null;

      const hasCurrentFilters =
        selectedCurrentReportFilters.length > 0 ||
        isNonPractitionerSignedCRSelected ||
        isPendingReviewSelectedCRSelected;

      const hasPreviousFilters =
        selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;

      if (hasCurrentFilters) {
        actualReportType = 'current';
      } else if (hasPreviousFilters) {
        actualReportType = 'previous';
      }
      // If neither has filters, actualReportType stays null

      setReportType(actualReportType);
      setTempCurrentReportFilters(selectedCurrentReportFilters);
      setTempPreviousReportFilters(selectedPreviousReportFilters);
      setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
      setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
      setTempRecentSignedSelected(isRecentSignedSelected);
      setTempDownloadedSelected(isDownloadedSelected);
      setRecentSignedSortBy(initialRecentSignedSortBy);
    }
  }, [
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy
  ]);

  const clinicOptions = useMemo(
    () =>
      globalFilterData?.clinics
        .map((clinic) => ({
          label: clinic.name,
          value: clinic.id,
          desc: clinic?.parent?.name ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [globalFilterData]
  );

  // Check if clinic options have multiple accounts
  const hasMultipleAccountsInOptions = useMemo(() => {
    const accounts = new Set(clinicOptions.map((clinic) => clinic.desc || 'No Account'));
    return accounts.size > 1;
  }, [clinicOptions]);

  // Check if selected clinics belong to different accounts
  const hasMultipleAccountsSelected = useMemo(() => {
    const accounts = new Set(
      filters.clinics.map((clinic) => {
        const option = clinicOptions.find((opt) => opt.value === clinic.value);
        return option?.desc || 'No Account';
      })
    );
    return accounts.size > 1;
  }, [filters.clinics, clinicOptions]);

  // Only show tooltip if not admin, multiple accounts exist in options, and multiple selected
  const shouldShowTooltip =
    !isUserAdmin && hasMultipleAccountsInOptions && hasMultipleAccountsSelected;

  const practitionerOptions = useMemo(() => {
    if (!practitionerData?.data) return [NULL_OPTION];

    const options = practitionerData.data
      .map((practitioner) => ({
        label: practitioner.name,
        value: practitioner.id
      }))
      .reduce<LabelAndValue[]>(
        (acc, current) =>
          acc.some((item) => item.value === current.value) ? acc : [...acc, current],
        []
      )
      .sort((a, b) => a.label.localeCompare(b.label));

    return [NULL_OPTION, ...options];
  }, [practitionerData]);

  const updateFilter = useCallback(
    (key: keyof FilterState) => (values: LabelAndValue[]) => {
      if (key === 'clinics' && !isUserAdmin && hasMultipleAccountsInOptions) {
        // Check if the newly selected clinics include options from different accounts
        const selectedAccounts = new Set(
          values.map((clinic) => {
            const option = clinicOptions.find((opt) => opt.value === clinic.value);
            return option?.desc || 'No Account';
          })
        );

        // If multiple accounts are selected, keep only the most recently selected clinic
        if (selectedAccounts.size > 1) {
          // Find the most recently added clinic (the one not in the previous selection)
          const previousValues = filters.clinics;
          const newClinic = values.find(
            (v) => !previousValues.some((prev) => prev.value === v.value)
          );

          if (newClinic) {
            // Get the account of the newly selected clinic
            const newAccount =
              clinicOptions.find((opt) => opt.value === newClinic.value)?.desc || 'No Account';

            // Filter to keep only clinics from the same account as the newly selected one
            const filteredClinics = values.filter((clinic) => {
              const option = clinicOptions.find((opt) => opt.value === clinic.value);
              const account = option?.desc || 'No Account';
              return account === newAccount;
            });

            setFilters((prev) => ({
              ...prev,
              practitioners: [],
              signingPractitioners: [],
              clinics: filteredClinics
            }));
            return;
          }
        }
      }

      setFilters((prev) => ({
        ...prev,
        practitioners: key === 'clinics' ? [] : prev.practitioners,
        signingPractitioners: key === 'clinics' ? [] : prev.signingPractitioners,
        [key]: values
      }));
    },
    [clinicOptions, filters.clinics, isUserAdmin, hasMultipleAccountsInOptions]
  );

  const handleReportFilterToggle = useCallback(
    (filterType: string) => {
      if (reportType === 'current') {
        setTempCurrentReportFilters((prev) =>
          prev.includes(filterType) ? prev.filter((f) => f !== filterType) : [...prev, filterType]
        );
      } else if (reportType === 'previous') {
        setTempPreviousReportFilters((prev) =>
          prev.includes(filterType) ? prev.filter((f) => f !== filterType) : [...prev, filterType]
        );
      }
    },
    [reportType]
  );

  const isAllReportFiltersSelected = useMemo(() => {
    if (!reportType) return false;

    const allBaseFiltersSelected = FILTER_OPTIONS.every((opt) =>
      reportType === 'current'
        ? tempCurrentReportFilters.includes(opt.value)
        : tempPreviousReportFilters.includes(opt.value)
    );

    if (reportType === 'current') {
      return (
        allBaseFiltersSelected &&
        (showNoReportOption ? tempNonPractitionerSelected && tempPendingReviewSelected : true)
      );
    } else {
      return (
        allBaseFiltersSelected &&
        (showPreviousReportOptions ? tempRecentSignedSelected && tempDownloadedSelected : true)
      );
    }
  }, [
    reportType,
    tempCurrentReportFilters,
    tempPreviousReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    showNoReportOption,
    showPreviousReportOptions
  ]);

  const handleSelectAllToggle = useCallback(() => {
    if (!reportType) return;

    if (isAllReportFiltersSelected) {
      if (reportType === 'current') {
        setTempCurrentReportFilters([]);
        if (showNoReportOption) setTempNonPractitionerSelected(false);
        if (showNoReportOption) setTempPendingReviewSelected(false);
      } else {
        setTempPreviousReportFilters([]);
        if (showPreviousReportOptions) {
          setTempRecentSignedSelected(false);
          setTempDownloadedSelected(false);
        }
      }
    } else {
      if (reportType === 'current') {
        setTempCurrentReportFilters(FILTER_OPTIONS.map((opt) => opt.value));
        if (showNoReportOption) setTempNonPractitionerSelected(true);
        if (showNoReportOption) setTempPendingReviewSelected(true);
      } else {
        setTempPreviousReportFilters(FILTER_OPTIONS.map((opt) => opt.value));
        if (showPreviousReportOptions) {
          setTempRecentSignedSelected(true);
          setTempDownloadedSelected(true);
        }
      }
    }
  }, [reportType, isAllReportFiltersSelected, showNoReportOption, showPreviousReportOptions]);

  const handleApply = useCallback(async () => {
    try {
      await updateGlobalFilter({
        organizationIds: filters.clinics.map((c) => c.value),
        practitionerIds: filters.practitioners.map((p) => p.value),
        deviceTypes: filters.deviceTypes.map((d) => d.value),
        vendors: filters.vendors.map((v) => v.value),
        signingPractitionerIds: filters.signingPractitioners.map((s) => s.value),
        patientStatuses: filters.patientStatuses.map((s) => s.value)
      }).unwrap();

      dispatch(
        setGlobalFilters({
          clinics: filters.clinics,
          practitioners: filters.practitioners,
          signingPractitioners: filters.signingPractitioners,
          deviceTypes: filters.deviceTypes,
          vendors: filters.vendors,
          patientStatuses: filters.patientStatuses
        })
      );

      if (onApplyReportFilters) {
        if (reportType === 'current') {
          onApplyReportFilters(
            tempCurrentReportFilters,
            tempNonPractitionerSelected,
            tempPendingReviewSelected,
            false,
            false,
            'current'
          );
        } else if (reportType === 'previous') {
          onApplyReportFilters(
            tempPreviousReportFilters,
            false,
            false,
            tempRecentSignedSelected,
            tempDownloadedSelected,
            'previous',
            tempRecentSignedSelected ? recentSignedSortBy : undefined
          );
        } else if (reportType === null) {
          onApplyReportFilters([], false, false, false, false, 'current');
        }
      }

      isDropdownOpenRef.current = false;
      close();
    } catch (error) {
      console.error('Failed to update filters:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update filters',
        color: 'red'
      });
    }
  }, [
    updateGlobalFilter,
    dispatch,
    filters,
    onApplyReportFilters,
    reportType,
    tempCurrentReportFilters,
    tempPreviousReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    recentSignedSortBy,
    close
  ]);

  const isApplyDisabled = useMemo(() => {
    const globalFiltersChanged =
      JSON.stringify(filters.clinics) !== JSON.stringify(defaultClinics) ||
      JSON.stringify(filters.practitioners) !== JSON.stringify(defaultPractitioners) ||
      JSON.stringify(filters.signingPractitioners) !==
        JSON.stringify(defaultSigningPractitioners) ||
      JSON.stringify(filters.deviceTypes) !== JSON.stringify(defaultDeviceTypes) ||
      JSON.stringify(filters.vendors) !== JSON.stringify(defaultVendors) ||
      JSON.stringify(filters.patientStatuses) !== JSON.stringify(defaultPatientStatuses);

    const reportFiltersChanged =
      reportType !== currentReportType ||
      JSON.stringify(tempCurrentReportFilters) !== JSON.stringify(selectedCurrentReportFilters) ||
      JSON.stringify(tempPreviousReportFilters) !== JSON.stringify(selectedPreviousReportFilters) ||
      tempNonPractitionerSelected !== isNonPractitionerSignedCRSelected ||
      tempPendingReviewSelected !== isPendingReviewSelectedCRSelected ||
      tempRecentSignedSelected !== isRecentSignedSelected ||
      tempDownloadedSelected !== isDownloadedSelected ||
      recentSignedSortBy !== initialRecentSignedSortBy;

    return !globalFiltersChanged && !reportFiltersChanged;
  }, [
    filters,
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses,
    reportType,
    currentReportType,
    tempCurrentReportFilters,
    selectedCurrentReportFilters,
    tempPreviousReportFilters,
    selectedPreviousReportFilters,
    tempNonPractitionerSelected,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    isRecentSignedSelected,
    tempDownloadedSelected,
    isDownloadedSelected,
    recentSignedSortBy,
    initialRecentSignedSortBy
  ]);

  const isClearAllDisabled = useMemo(() => {
    const isGlobalFiltersEmpty =
      filters.clinics.length === 0 &&
      filters.practitioners.length === 0 &&
      filters.signingPractitioners.length === 0 &&
      filters.deviceTypes.length === 0 &&
      filters.vendors.length === 0 &&
      filters.patientStatuses.length === 0;

    const isReportFiltersEmpty =
      reportType === null &&
      tempCurrentReportFilters.length === 0 &&
      tempPreviousReportFilters.length === 0 &&
      !tempNonPractitionerSelected &&
      !tempPendingReviewSelected &&
      !tempRecentSignedSelected &&
      !tempDownloadedSelected;

    return isGlobalFiltersEmpty && isReportFiltersEmpty;
  }, [
    filters,
    reportType,
    tempCurrentReportFilters.length,
    tempPreviousReportFilters.length,
    tempNonPractitionerSelected,
    tempRecentSignedSelected,
    tempPendingReviewSelected,
    tempDownloadedSelected
  ]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;

    if (globalFilters?.clinics && globalFilters.clinics.length > 0) count += 1;
    if (globalFilters?.practitioners && globalFilters.practitioners.length > 0) count += 1;
    if (globalFilters?.signingPractitioners && globalFilters.signingPractitioners.length > 0)
      count += 1;
    if (globalFilters?.deviceTypes && globalFilters.deviceTypes.length > 0) count += 1;
    if (globalFilters?.vendors && globalFilters.vendors.length > 0) count += 1;
    if (globalFilters?.patientStatuses && globalFilters.patientStatuses.length > 0) count += 1;

    const hasCurrentFilters =
      selectedCurrentReportFilters.length > 0 ||
      isNonPractitionerSignedCRSelected ||
      isPendingReviewSelectedCRSelected;

    const hasPreviousFilters =
      selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;

    if (hasCurrentFilters || hasPreviousFilters) {
      count += 1;
    }

    return count;
  }, [
    globalFilters,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected
  ]);

  const onOpenClick = async () => {
    if (reportUpdateFields && Object.keys(reportUpdateFields).length > 0) {
      saveUpdatedReportFields();
      await delay(1000);
    }
    isDropdownOpenRef.current = true;
    open();
  };

  const onCloseClick = useCallback(() => {
    isDropdownOpenRef.current = false;
    close();

    // Determine the actual report type based on what filters are currently applied
    let actualReportType: 'current' | 'previous' | null = null;

    const hasCurrentFilters =
      selectedCurrentReportFilters.length > 0 ||
      isNonPractitionerSignedCRSelected ||
      isPendingReviewSelectedCRSelected;

    const hasPreviousFilters =
      selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;

    if (hasCurrentFilters) {
      actualReportType = 'current';
    } else if (hasPreviousFilters) {
      actualReportType = 'previous';
    }
    // If neither has filters, actualReportType stays null

    setFilters({
      clinics: defaultClinics,
      practitioners: defaultPractitioners,
      signingPractitioners: defaultSigningPractitioners,
      deviceTypes: defaultDeviceTypes,
      vendors: defaultVendors,
      patientStatuses: defaultPatientStatuses
    });
    setReportType(actualReportType);
    setTempCurrentReportFilters(selectedCurrentReportFilters);
    setTempPreviousReportFilters(selectedPreviousReportFilters);
    setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
    setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
    setTempRecentSignedSelected(isRecentSignedSelected);
    setTempDownloadedSelected(isDownloadedSelected);
    setRecentSignedSortBy(initialRecentSignedSortBy);
    setShowRecentSignedSubmenu(false);
  }, [
    close,
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors,
    defaultPatientStatuses,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy
  ]);

  const clearAllFilters = useCallback(() => {
    setFilters({
      clinics: [],
      practitioners: [],
      signingPractitioners: [],
      deviceTypes: [],
      vendors: [],
      patientStatuses: []
    });
    setReportType(null);
    setTempCurrentReportFilters([]);
    setTempPreviousReportFilters([]);
    setTempNonPractitionerSelected(false);
    setTempPendingReviewSelected(false);
    setTempRecentSignedSelected(false);
    setTempDownloadedSelected(false);
    setRecentSignedSortBy('SIGNED_DATE');
  }, []);

  const activeReportFilters =
    reportType === 'current' ? tempCurrentReportFilters : tempPreviousReportFilters;

  return (
    <Popover
      position="bottom-end"
      shadow="md"
      opened={opened}
      width={380}
      onClose={onCloseClick}
      withinPortal>
      <Popover.Target>
        <Button variant="transparent" onClick={onOpenClick}>
          FILTERS
          {activeFiltersCount > 0 && (
            <Badge variant="filled" size="xs" circle pos="absolute" top={0} right={5}>
              {activeFiltersCount}
            </Badge>
          )}
          <span style={{ marginLeft: '10px' }}>
            <FilterMenuIcon />
          </span>
        </Button>
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
        <Box
          style={{
            backgroundColor: '#C9BBBE',
            borderBottom: '1px solid #E0E0E0'
          }}>
          <Group justify="space-between" p="md" pb="sm">
            <Box fw={600} fz="lg" c="#572B3E">
              Filters
            </Box>
          </Group>
        </Box>

        <ScrollArea h={500} type="auto" offsetScrollbars>
          <Box px="md">
            <Stack gap="xs" py="md">
              <Box>
                <MultiSelectDropdownWithSearch
                  options={clinicOptions}
                  defaultValues={filters.clinics}
                  onChange={updateFilter('clinics')}
                  label="Clinics"
                  maxItemsToDisplay={1}
                  showTooltip={!isUserAdmin && hasMultipleAccountsInOptions}
                  tooltipMessage="Selecting a clinic from a different account will clear your previous clinic selection"
                  infoMessage={
                    !isUserAdmin && hasMultipleAccountsInOptions
                      ? 'Selecting a clinic in another account resets your selection'
                      : null
                  }
                />
              </Box>

              <FilterOption
                options={practitionerOptions}
                defaultValues={filters.practitioners}
                onChange={updateFilter('practitioners')}
                label="Following Practitioners"
                isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
              />
              <FilterOption
                options={practitionerOptions}
                defaultValues={filters.signingPractitioners}
                onChange={updateFilter('signingPractitioners')}
                label="Signing Practitioners"
                isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
              />
              <FilterOption
                options={DEVICE_TYPE_OPTIONS}
                defaultValues={filters.deviceTypes}
                onChange={updateFilter('deviceTypes')}
                label="Device Types"
              />
              <FilterOption
                options={VENDOR_OPTIONS}
                defaultValues={filters.vendors}
                onChange={updateFilter('vendors')}
                label="Vendors"
              />
              <Box>
                <InputLabel label="Status" name="patient-status" labelColor="#A27E8A" />
                <Group gap="md">
                  {PATIENT_STATUS_OPTIONS.map((option) => (
                    <Checkbox
                      key={option.value}
                      label={option.label}
                      checked={filters.patientStatuses.some((s) => s.value === option.value)}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        if (checked) {
                          updateFilter('patientStatuses')([...filters.patientStatuses, option]);
                        } else {
                          updateFilter('patientStatuses')(
                            filters.patientStatuses.filter((s) => s.value !== option.value)
                          );
                        }
                      }}
                    />
                  ))}
                </Group>
              </Box>
            </Stack>

            <Stack gap="xs">
              <Box>
                <InputLabel label="Report Type" name="report-type" labelColor="#A27E8A" />
                <Select
                  value={REPORT_TYPE_OPTIONS.find(
                    (option) => option.value === (reportType || 'none')
                  )}
                  onChange={(option) => {
                    const value = option?.value;
                    setReportType(value === 'none' ? null : (value as 'current' | 'previous'));
                  }}
                  options={REPORT_TYPE_OPTIONS}
                  placeholder="Select report type"
                  styles={reportTypeSelectStyles}
                  menuPortalTarget={document.body}
                  theme={(theme) => ({
                    ...theme,
                    colors: {
                      ...theme.colors,
                      primary: plum[6],
                      primary75: plum[5],
                      primary50: plum[4],
                      primary25: plum[2]
                    }
                  })}
                />
              </Box>

              {reportType && (
                <Stack mt="xs">
                  <Group justify="space-between">
                    <LabelText
                      label={reportType === 'current' ? 'Current Reports' : 'Previous Reports'}
                    />
                    <Button size="xs" variant="subtle" onClick={handleSelectAllToggle} color="plum">
                      {isAllReportFiltersSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Group>

                  <Divider mx={-16} mt={-16} />

                  <Stack gap="xs">
                    {FILTER_OPTIONS.map(({ value, label }) => (
                      <Box key={value}>
                        <Checkbox
                          checked={activeReportFilters.includes(value)}
                          onChange={() => handleReportFilterToggle(value)}
                          label={label}
                          mb="xs"
                        />
                        <Divider />
                      </Box>
                    ))}

                    {reportType === 'current' && showNoReportOption && (
                      <>
                        <Box>
                          <Checkbox
                            checked={tempNonPractitionerSelected}
                            onChange={() => setTempNonPractitionerSelected((prev) => !prev)}
                            label="Pending Physician Review"
                            mb="xs"
                          />
                          <Divider />
                        </Box>
                        <Box>
                          <Checkbox
                            checked={tempPendingReviewSelected}
                            onChange={() => setTempPendingReviewSelected((prev) => !prev)}
                            label="Regular Pending Review"
                            mb="xs"
                          />
                          <Divider />
                        </Box>
                      </>
                    )}

                    {reportType === 'previous' && showPreviousReportOptions && (
                      <>
                        <Box>
                          <Group justify="space-between" wrap="nowrap">
                            <Checkbox
                              checked={tempRecentSignedSelected}
                              onChange={() => setTempRecentSignedSelected((prev) => !prev)}
                              label="Recently Signed (Last 30 days)"
                              mb="xs"
                              styles={{
                                label: { flex: 1 }
                              }}
                            />
                            {tempRecentSignedSelected && (
                              <Box
                                onClick={() => setShowRecentSignedSubmenu((prev) => !prev)}
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '4px',
                                  marginTop: '-6px',
                                  transition: 'transform 0.2s'
                                }}>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  style={{
                                    transform: showRecentSignedSubmenu
                                      ? 'rotate(90deg)'
                                      : 'rotate(0deg)',
                                    transition: 'transform 0.2s'
                                  }}>
                                  <polyline points="9 18 15 12 9 6" />
                                </svg>
                              </Box>
                            )}
                          </Group>
                          <Divider />
                          <Collapse in={tempRecentSignedSelected && showRecentSignedSubmenu}>
                            <Box pl="lg" py="xs">
                              <Radio.Group
                                value={recentSignedSortBy}
                                onChange={(value) =>
                                  setRecentSignedSortBy(value as 'SIGNED_DATE' | 'DATE_OF_SERVICE')
                                }>
                                <Stack gap="xs">
                                  <Radio value="SIGNED_DATE" label="Signed Date" />
                                  <Divider />
                                  <Radio value="DATE_OF_SERVICE" label="Date of Service" />
                                </Stack>
                              </Radio.Group>
                            </Box>
                          </Collapse>
                          <Divider />
                        </Box>

                        <Box>
                          <Checkbox
                            checked={tempDownloadedSelected}
                            onChange={() => setTempDownloadedSelected((prev) => !prev)}
                            label="Not downloaded"
                          />
                        </Box>
                      </>
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #E0E0E0',
            backgroundColor: 'white'
          }}>
          <Group justify="space-between">
            <Button variant="subtle" onClick={clearAllFilters} disabled={isClearAllDisabled}>
              Clear All
            </Button>
            <Group gap="xs">
              <Button variant="default" onClick={onCloseClick}>
                Cancel
              </Button>
              <Tooltip
                label="Selecting a clinic from a different account will clear your previous clinic selection"
                disabled={!shouldShowTooltip}
                position="top"
                withArrow
                multiline
                w={220}
                styles={{
                  tooltip: {
                    backgroundColor: plum[9],
                    color: 'white'
                  }
                }}>
                <Button
                  onClick={handleApply}
                  disabled={isApplyDisabled}
                  loading={isUpdatingFilter}
                  style={{
                    backgroundColor: plum[8],
                    color: 'white',
                    cursor: shouldShowTooltip ? 'help' : 'pointer'
                  }}>
                  Apply
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default memo(CombinedFiltersDropdown);
